import React from 'react';

import t from 'utils/translate';
import Text from 'admin/components/common/Text';

import Button from 'admin/components/common/Button';
import ParamsTable from 'admin/components/common/Intregrations/ParamsTable';
import { Select, TextField } from 'admin/components/common/Form';
import { Icon } from 'admin/components/common/Icon';
import { Modal, ModalBody, ModalFooter } from 'admin/components/common/Modal';
import { ModalManagerProvidedProps } from 'admin/components/common/ModalManager';
import { MODAL } from 'admin/constants/common';
import { parseGAParams } from 'utils/ga-params';
import { CUSTOM_TRACKER_OPTIONS } from 'utils/facades/integrations-facade';
import { useAdminSelector } from 'src/routes/admin/reducers';
import { selectEditableElement } from 'admin/reducers/card-editor-extra/selectors';
import { selectCollections } from 'admin/reducers/cms/selectors';
import { COMPONENT_TYPE } from 'common/constants';
import cms from 'common/utils/cms';
import { getCollectionFieldOptions } from 'admin/components/pages/Story/CardEditor/Inspector/CmsSettings/utils';
import type { GAParams } from '../types';

import css from './Modal.scss';

const modalText = (path: string): string => t(`story.gaParametersModal.${path}`);

export type GAParametersModalData = {
	title?: string;
	label?: string;
	hint?: string;
	gaParams?: string;
	onSubmit: (gaParams: GAParams) => unknown;
	onClear: () => unknown;
};

type Props = ModalManagerProvidedProps<MODAL.GA_PARAMS>;

const GASettingsModal = ({ open, data, ...props }: Props) => {
	const { title, gaParams } = data;
	const theme = 'dark';
	const element = useAdminSelector(selectEditableElement);
	const cmsCollections = useAdminSelector(selectCollections);
	const [parsedParams] = React.useState(() => parseGAParams(gaParams ?? ''));
	const [actionName, setActionName] = React.useState(parsedParams.actionName);
	const [paramName, setParamName] = React.useState('');
	const [paramValue, setParamValue] = React.useState('');
	const [error, setError] = React.useState<{ actionName: string; name: string; value: string }>({
		actionName: '',
		name: '',
		value: '',
	});
	const [params, setParams] = React.useState(parsedParams.params);

	// Compute select options: grouped if CMS_REPEATER parent exists, flat otherwise
	const selectOptions = React.useMemo(() => {
		const repeaterCollectionId = element?.inheritance?.parent?.find(
			parent => parent.type === COMPONENT_TYPE.CMS_REPEATER
		)?.inheritance?.componentProps?.collectionId?.[0]?.value;
		const collection = repeaterCollectionId ? cmsCollections.find(c => c.id === repeaterCollectionId) : null;
		const cmsFieldOptions = collection ? getCollectionFieldOptions(collection.fields, 'any') : [];

		if (cmsFieldOptions.length === 0) {
			return Object.values(CUSTOM_TRACKER_OPTIONS);
		}

		return [
			{
				label: 'Common',
				options: Object.values(CUSTOM_TRACKER_OPTIONS),
			},
			{
				label: 'CMS',
				options: cmsFieldOptions.map(option => ({
					label: option.label,
					value: cms.createReference(collection!.id, cms.DataIdSource.BY_CONTEXT, option.value),
				})),
			},
		];
	}, [element.inheritance?.parent, cmsCollections]);

	const onActionNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = e.currentTarget;
		setActionName(value);
	};

	const onParamNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const val = e.currentTarget.value;

		if (/(\s)/g.test(val)) {
			setError({ ...error, name: modalText('errors.invalid') });
		} else if (params.find(p => p.name === val)) {
			setError({ ...error, name: modalText('errors.alreadyExist') });
		} else if (error.name) {
			setError({ ...error, name: '' });
		}

		setParamName(e.currentTarget.value);
	};

	const onParamValueChange = (value: string | number | undefined) => {
		setParamValue(value?.toString() ?? '');
	};

	const onAddParamClick = () => {
		setParams(p => [...p, { name: paramName, value: paramValue }]);
		setParamName('');
		setParamValue('');
	};

	const onDeleteParamBtnClick = (targetIndex: number) => {
		setParams(p => p.filter((_, i) => i !== targetIndex));
	};

	const onCancel = () => {
		props.close();
	};

	const onClear = async () => {
		await data.onClear();
		props.close();
	};

	const onSubmit = async () => {
		await data.onSubmit({
			actionName,
			params,
		});
		props.close();
	};

	const hasChanges = React.useMemo(() => {
		const actionNameChanged = actionName !== parsedParams.actionName;
		const paramsChanged =
			params.length !== parsedParams.params.length ||
			params.some((p, i) => {
				const originalParam = parsedParams.params[i];
				return !originalParam || p.name !== originalParam.name || p.value !== originalParam.value;
			});
		return actionNameChanged || paramsChanged;
	}, [actionName, params, parsedParams.actionName, parsedParams.params]);

	const renderParamsTable = () => {
		const readableParams = params.map(p => {
			const cmsRef = cms.parseReference(p.value, false);
			return {
				name: p.name,
				value: cmsRef ? `$CMS.${cmsRef.dataKey}` : p.value,
			};
		});

		return (
			<ParamsTable
				title={modalText('tableTitle')}
				theme={theme}
				columns={[
					{ label: modalText('colNameTitle'), fieldKey: 'name' },
					{ label: modalText('colValueTitle'), fieldKey: 'value' },
					{ label: ' ', fieldKey: 'action' },
				]}
				data={readableParams.map((p, i) => ({
					...p,
					action: (
						<Button
							view="empty"
							color="danger-ds"
							shape="circle"
							size="tiny"
							onClick={() => onDeleteParamBtnClick(i)}
						>
							<Icon type="minus-rounded" color="currentColor" />
						</Button>
					),
				}))}
			/>
		);
	};

	const renderInputParam = () => {
		return (
			<div className={css.inputParam}>
				<TextField
					name="name"
					placeholder="campaign"
					isRequired
					value={paramName}
					isLabelUppercase={false}
					error={error.name}
					theme={theme}
					label={modalText('fields.paramName.label')}
					onChange={onParamNameChange}
					autoComplete="off"
				/>
				<Select
					label={{ children: modalText('fields.paramValue.label') }}
					options={selectOptions}
					value={paramValue}
					placeholder={modalText('fields.paramValue.placeholder')}
					isCreatable
					theme={theme}
					error={error.value}
					eventListeners={{
						onChange: onParamValueChange,
					}}
				/>

				<Button
					view="empty"
					color="primary"
					shape="circle"
					size="small"
					style={{ marginTop: 34 }}
					disabled={!paramName || !paramValue || error.name !== '' || error.value !== ''}
					onClick={onAddParamClick}
				>
					<Icon type="plus-rounded" color="currentColor" />
				</Button>
			</div>
		);
	};

	const renderContent = () => {
		return (
			<>
				<ModalBody>
					<TextField
						name="action"
						placeholder="custom_event"
						isRequired
						value={actionName}
						isLabelUppercase={false}
						error={error.actionName}
						theme={theme}
						label={modalText('fields.actionName.label')}
						onChange={onActionNameChange}
						autoComplete="off"
					/>
					{renderInputParam()}
					{renderParamsTable()}
				</ModalBody>
				<ModalFooter>
					<Button view="primary" type="button" onClick={onSubmit} disabled={!hasChanges}>
						{modalText('save')}
					</Button>
					{Boolean(gaParams?.trim()) && (
						<Button view="danger" type="button" onClick={onClear}>
							{modalText('clear')}
						</Button>
					)}
					<Button view="secondary-gray" onClick={onCancel}>
						{modalText('cancel')}
					</Button>
				</ModalFooter>
			</>
		);
	};

	return (
		<Modal
			onCancel={onCancel}
			maskColor="black"
			theme="dark"
			open={open}
			destroyOnClose
			width={parseInt(css.modalWidth, 10)}
			className={css.modal}
			transitionName=""
			title={title ? <Text size={Text.size.subtitle} weight={Text.weight.semibold} compact text={title} /> : null}
		>
			<div>{renderContent()}</div>
		</Modal>
	);
};

export default GASettingsModal;
